import {
  BookingFormValues,
  Option,
  TMoney,
  EnumOptionKind,
  Image,
  Tag,
  Collection,
} from 'src/types';
import { numericFormatter } from 'react-number-format';
import currencies from 'src/data/currencies';
import { YOUTUBE_ID_REGEX, DBDateFormat } from 'src/lib/constants';
import dayjs from 'dayjs';
export * from './order_item';
export * from './account';
export * from './env';
export * from './image';
export * from './option';
export * from './chat';
export * from './pricing';
export * from './formik';
export * from './ui';
import { upperFirst, snakeCase, min, flatten, sortBy } from 'lodash-es';
import Cookies from 'js-cookie';
import { StaticImageData } from 'next/image';
import { AppRoutes } from 'src/lib/routes';
import intlTelInput from 'intl-tel-input';
import { ParsedUrlQuery } from 'querystring';

const AFTER_LOGIN_EXCLUDED_PATHS = [ '/users/login', '/404']

export function humanize(str: string) {
  return upperFirst(snakeCase(str).replace(/_/g, ' '))
}

export function humanizeMoney(amount: TMoney): string {
  const currency = currencies.find((o) => amount.currency === o.code);

  if (!currency) {
    throw Error(`${amount.currency} is not supported`);
  }

  return numericFormatter(String(amount.amount), {
    thousandSeparator: currency.thousandsSeparator,
    decimalSeparator: currency.decimalMark,
    decimalScale: Number(currency.exponent),
    prefix: currency.prefix ? currency.prefix : '',
    suffix: currency.suffix ? currency.suffix : '',
  });
}

export function getEleOffset(el: Element) {
  const box = el.getBoundingClientRect();
  const docElem = document.documentElement;
  return {
    top: box.top + window.pageYOffset - docElem.clientTop,
    left: box.left + window.pageXOffset - docElem.clientLeft,
  };
}

export function scrollTo(el: Element, adjustDistance: number = 0) {
  const offset = getEleOffset(el);
  window.scrollTo({
    left: 0,
    top: offset.top + adjustDistance,
    behavior: 'smooth',
  });
}

export const scrollToEle = (selector: string, adjustDistance?: number) => {
  return () => {
    const ele = document.querySelector(selector);
    if (ele instanceof HTMLElement) {
      scrollTo(ele, adjustDistance || 0);
    }
  };
};

export const getNumberOfWeekdaysBetweenDates = (dayOfWeek: number, startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
  let numberOfWeeks = Math.abs(startDate.diff(endDate, "week"));
  const numberOfDays = Math.abs(startDate.diff(endDate, "day"));
  const remainDays = numberOfDays - numberOfWeeks * 7;
  let diff = endDate.day() - dayOfWeek;
  if (diff < 0) diff += 7;
  if (remainDays >= diff) numberOfWeeks += 1;
  return numberOfWeeks;
}

export const generateArrayDates = (
  from: string | Date,
  to: string | Date,
  weekdays?: number[] | null
) => {
  var ary = [];
  var currentDate = dayjs(from);
  var endDate = dayjs(to);
  var maxEndDate = dayjs().add(1, 'year').endOf('year');
  endDate = endDate.isSameOrBefore(maxEndDate) ? endDate : maxEndDate;
  var parsedWeekdays = weekdays && weekdays.map((v) => Number(v));
  while (currentDate.isSameOrBefore(endDate)) {
    if (!parsedWeekdays || parsedWeekdays.includes(currentDate.day())) {
      ary.push(currentDate.format(DBDateFormat));
    }
    currentDate = currentDate.add(1, 'day');
  }
  return ary;
};

export function isStayOrFlexible(option: Option) {
  return [EnumOptionKind.flexibleAdventure, EnumOptionKind.stay].includes(
    option.kind as EnumOptionKind
  );
}

export function isValidPhoneNumber(number: string, inputEle?: HTMLInputElement) {
  const instance = inputEle ? intlTelInput.getInstance(inputEle) : undefined
  return instance
    ? instance.isValidNumber() || false
    : !!number;
}

export function getCsrfToken() {
  return Cookies.get('next_csrf_token');
}

export function convertStaticImageToUploadImage(image: StaticImageData, name?: string): Image {
  return {
    id: 0,
    name: name || 'default',
    contentType: 'image/jpg',
    caption: undefined,
    url: image.src,
    lgUrl: image.src,
    mdUrl: image.src,
    webpXlUrl: image.src,
    webpLgUrl: image.src,
    webpMdUrl: image.src,
    webpSmUrl: image.src,
  };
}

export function getSelectedDates(bookingFormValues: BookingFormValues, option: Option) {
  if (bookingFormValues.startDate && bookingFormValues.endDate) {
    return generateArrayDates(bookingFormValues.startDate, bookingFormValues.endDate).slice(
      0,
      option.kind === EnumOptionKind.flexibleAdventure ? undefined : -1
    );
  } else if (bookingFormValues.startDate) {
    return generateArrayDates(bookingFormValues.startDate, bookingFormValues.startDate);
  } else {
    return [];
  }
}

export function groupCollectionsByTag(collections: Collection[]) {
  const tags = flatten(collections.map((o) => [o.tag, o.tag?.parent])).filter(
    (tag) => !!tag
  ) as Tag[];

  const groupedCollections = collections
    .filter((o) => o.tag)
    .reduce((memo, collection) => {
      const tag = collection.tag as Tag;
      const parentId = tag.parent ? tag.parent.id : tag.parentId ? null : tag.id;
      if (parentId) {
        memo[parentId] ||= [];
        memo[parentId] = memo[parentId].concat([collection]);
      }

      return memo;
    }, {} as { [key: number]: Collection[] });

  const sortedParentTags = sortBy(
    Object.keys(groupedCollections)
      .map((parentTagId) => {
        const parentTag = tags.find((o) => o.id === Number(parentTagId));

        if (!parentTag) return null;

        return {
          ...parentTag,
          collections: sortBy(groupedCollections[Number(parentTagId)], [
            (o) => (o.tag?.parentId ? 1 : 0),
            'name',
          ]),
        };
      })
      .filter((v) => v) as (Tag & { collections: Collection[] })[],
    ['name']
  );

  return sortedParentTags;
}

export function getAfterLoginUrlObject(routerQuery: ParsedUrlQuery, defaultUrl: string = AppRoutes.rootRoute({}).toPath()) {
  const { redirect_path: redirectPath, redirect_path_query: redirectPathQuery } = routerQuery

  let query: Record<string, any> = {}
  try {
    query = JSON.parse(String(redirectPathQuery))
  } catch {
  }
  return redirectPath && !AFTER_LOGIN_EXCLUDED_PATHS.includes(redirectPath as string) ? {
    pathname: String(redirectPath),
    query: query
  } : {
    pathname: defaultUrl
  }
}

export function isProduction() {
  return process.env.NEXT_PUBLIC_APP_ENV === 'production'
}

export function getFacebookShareUrl(url: string, redirectUrl: string, isMobile: boolean = false) {
  return isMobile ? `fb-messenger://share/?link=${url}` : `
    http://www.facebook.com/dialog/send?app_id=${process.env.NEXT_PUBLIC_FACEBOOK_APP_ID}&link=${url}&redirect_uri=${encodeURIComponent(redirectUrl)}
  `
}

export function isYoutubeVideo(videoId: string | number) {
  return YOUTUBE_ID_REGEX.test(String(videoId))
}
